<script setup lang="ts" generic="T">
import { Popover, PopoverTrigger, PopoverContent } from './popover';
import { Command, CommandInput, CommandEmpty, CommandList, CommandGroup, CommandItem } from './command';
import { Button } from './button';
import { computed, ref, watch } from 'vue';
import { cn } from './ui-utils.ts';
import type { HTMLAttributes } from 'vue';

const props = defineProps<{
  /** Accept immutable reference catalogs without forcing callers to copy them into mutable arrays. */
  options: readonly T[];
  toKey: (item: T) => string | number;
  toLabel: (item: T) => string;
  placeholder: string;
  class?: HTMLAttributes['class'];
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  disablePortal?: boolean;
}>();

const chosen = defineModel<T | null>({ required: true });

const open = ref(false);

const chosenKey = computed<string | null>({
  get() {
    return chosen.value ? String(props.toKey(chosen.value)) : null;
  },
  set(key) {
    chosen.value = key === null ? null : (props.options.find((option) => String(props.toKey(option)) === key) ?? null);
  },
});

function choose(option: T) {
  chosen.value = option;
  open.value = false;
}

function optionKey(option: T): string {
  return String(props.toKey(option));
}

watch(
  () => props.disabled,
  (disabled) => {
    if (disabled) {
      open.value = false;
    }
  },
);
</script>

<template>
  <Popover v-model:open="open">
    <PopoverTrigger asChild>
      <Button
        type="button"
        variant="outline"
        role="combobox"
        :aria-expanded="open"
        :disabled="disabled"
        :class="cn('w-[200px] justify-between overflow-hidden', props.class)"
      >
        <span class="truncate">{{ chosen ? toLabel(chosen) : placeholder }}</span>

        <i class="fa-light fa-angles-up-down ml-2 h-4 w-4 shrink-0 opacity-50"></i>
      </Button>
    </PopoverTrigger>
    <PopoverContent :disabled="disablePortal" class="z-[9053] w-(--reka-popover-trigger-width) min-w-[200px] p-0">
      <Command v-model="chosenKey" selectionBehavior="replace">
        <CommandInput :placeholder="searchPlaceholder ?? 'Search...'" :displayValue="() => ''" />
        <CommandEmpty>
          <slot name="empty">{{ emptyText ?? 'No results found.' }}</slot>
        </CommandEmpty>
        <CommandList>
          <CommandGroup>
            <CommandItem
              v-for="option of options"
              :key="optionKey(option)"
              :value="optionKey(option)"
              :textValue="toLabel(option)"
              @select="choose(option)"
            >
              <i
                class="fa-light fa-check"
                :class="cn('mr-2 h-4 w-4', chosenKey === optionKey(option) ? 'opacity-100' : 'opacity-0')"
              ></i>
              {{ toLabel(option) }}
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </Command>
    </PopoverContent>
  </Popover>
</template>
